import { SHELL_BUNDLE_NAMES } from "@slot/game-shell";
import type { GameAudioOverrides } from "@slot/game-shell";
import { CANNON_TUNING } from "../features/cannon/cannonTuning";

const CANNON_FIRE_SOUND_IDS = CANNON_TUNING.fireSoundIds;

export function createGameAudioOverrides(): GameAudioOverrides {
  const AUDIO_BUNDLE = {
    kind: "local" as const,
    name: SHELL_BUNDLE_NAMES.audio,
  };

  return {
    mapping: {
      "ui.click": "ui_click",

      // The grid's land and removal events → soundId: mechanic events, not every
      // slot's, so not in the shell's DefaultAudioMapping. Nothing emits either yet;
      // the reels play `column_drop` themselves, once per landed column (`DropThud`).
      "grid.column_drop": "column_drop",
      "grid.remove_symbols": "grid_remove_symbols",

      // slot-cannon's feature events → soundId, here rather than in the shell's
      // DefaultAudioMapping: the multiplier/scatter mechanics + win-highlight
      // stinger are this title's feature set.
      "grid.win_highlight": "grid_win_highlight",
      // A tumble's win landing in the win meter (the shell's `WinSequencePresenter.presentTotalWinAmount`).
      "grid.win_collect": "grid_multiplier_collect",
      "grid.scatter_drop": "grid_scatter_drop",
      "grid.multiplier_drop": "grid_multiplier_drop",
      "grid.scatter_collect": "grid_scatter_collect",
      "grid.multiplier_collect": "grid_multiplier_collect",
    },

    featureStateMusicMap: {
      super_bonus_buy: "music_feature_free_spins",
    },

    ambientTrackId: "music_ambient",

    // Game-only sounds that need to be ready on first call. The shell's hardcoded
    // critical-sound list doesn't cover game-specific assets, so without this the
    // first one-shot would be silent (decode kicks off but the play attempt is lost).
    preloadIds: [
      // Character / win-highlight / feature stingers (game-only sounds).
      "character_multiplier_drop",
      "grid_win_highlight",
      ...CANNON_FIRE_SOUND_IDS,
      "grid_chance_collect",
      "feature_trigger_panel_open",
      "feature_trigger_panel_close",

      // The land thud + slot-cannon's grid feature sounds.
      // Without these, first-play of these one-shots would be silent during
      // on-demand decode.
      "column_drop",
      "grid_remove_symbols",
      "grid_scatter_drop",
      "grid_multiplier_drop",
      "grid_scatter_collect",
      "grid_multiplier_collect",

      // Reel spin-roll loops — preload so the loop buffer is ready when the spin
      // starts (else startSfxLoop just kicks off a decode, no first-spin roll).
      "reel_roll",
      "reel_anticipation_roll",

      // slot-cannon's feature music — preload so the feature panels' music
      // changes start on their first call (a track not yet decoded does not start).
      "music_feature_free_spins",
      "music_feature_idle",
      "music_feature_finished_intro",
    ],

    // Per-id partial overrides applied AFTER bank registration. Shallow-merges
    // into the existing asset's `defaultOpts` — useful for tweaking individual
    // fields (most commonly `gain`) on shell-registered sounds without
    // re-declaring the whole asset.
    //
    // Format:
    //   defaultOptsOverrides: {
    //     music_base: { gain: 0.8 },
    //     spin_start: { gain: 1.3 },
    //   },
    //
    // Drop tweaks here when remix decisions require per-id gain/priority/etc.
    defaultOptsOverrides: {
      spin_start: { gain: 1.3 },
    },

    banks: [
      {
        id: "game-audio",
        assets: [
          {
            id: "music_ambient",
            locator: {
              kind: "bundle_audio",
              bundle: AUDIO_BUNDLE,
              key: "audio/music_ambient",
            },
            category: "ambient",
            defaultOpts: {
              gain: 1,
            },
          },
          {
            id: "character_multiplier_drop",
            locator: {
              kind: "bundle_audio",
              bundle: AUDIO_BUNDLE,
              key: "audio/character_multiplier_drop",
            },
            category: "sfx",
            defaultOpts: {
              priority: 20,
              stopOnSlam: false,
              protectFromSteal: false,
              gain:0.6,
            },
          },
          // ── The land thud ──
          // Once per landed column (`DropThud`) unless a symbol's own land sound replaces it.
          {
            id: "column_drop",
            locator: { kind: "bundle_audio", bundle: AUDIO_BUNDLE, key: "audio/column_drop" },
            category: "sfx",
            defaultOpts: {
              priority: 5,
              stopOnSlam: false,
              protectFromSteal: false,
              randomPlaybackRateMin: 0.9,
              randomPlaybackRateMax: 1.1,
            },
          },

          // ── Reel-mechanic SFX (spin-roll loops) ──
          // Looping drum SFX driven by ReelSpinMechanic via the shell SFX-loop facility
          // (startSfxLoop/stopSfxLoop). reel_roll runs the whole spin;
          // reel_anticipation_roll runs during the tease, with reel_roll ducked
          // under it (REEL_MOTION_TUNING.roll.anticipationDuckScale). gain is the
          // loop's base level (tune to sit under the land sounds).
          {
            id: "reel_roll",
            locator: { kind: "bundle_audio", bundle: AUDIO_BUNDLE, key: "audio/reel_roll" },
            category: "sfx",
            defaultOpts: { priority: 5, stopOnSlam: false, protectFromSteal: false, gain: 3 },
          },
          {
            id: "reel_anticipation_roll",
            locator: { kind: "bundle_audio", bundle: AUDIO_BUNDLE, key: "audio/reel_anticipation_roll" },
            category: "sfx",
            defaultOpts: { priority: 5, stopOnSlam: false, protectFromSteal: false, gain: 3 },
          },
          // Mapped to `grid.remove_symbols`, which nothing emits: silent in this title.
          {
            id: "grid_remove_symbols",
            locator: { kind: "bundle_audio", bundle: AUDIO_BUNDLE, key: "audio/grid_remove_symbols" },
            category: "sfx",
            defaultOpts: {
              priority: 5,
              stopOnSlam: false,
              protectFromSteal: false,
            },
          },

          // ── slot-cannon feature SFX (win highlight, cannon, chance, scatter, multiplier, trigger panel) ──
          // This title's own feature audio. Other slot titles using
          // different feature mechanics will replace these or omit them entirely.
          // One per win highlight, through the `"grid.win_highlight"` event.
          {
            id: "grid_win_highlight",
            locator: { kind: "bundle_audio", bundle: AUDIO_BUNDLE, key: "audio/grid_win_highlight" },
            category: "sfx",
            defaultOpts: {
              priority: 5,
              stopOnSlam: false,
              protectFromSteal: false,
              gain:6
            },
          },
          // The cannon firing on a win (shot + reload in one file), at the shot clip's start: one
          // take per shot of a spin, each a semitone higher (CANNON_TUNING.fireSoundIds).
          ...CANNON_FIRE_SOUND_IDS.map((id) => ({
            id,
            locator: { kind: "bundle_audio" as const, bundle: AUDIO_BUNDLE, key: `audio/${id}` },
            category: "sfx" as const,
            defaultOpts: {
              priority: 5,
              stopOnSlam: false,
              protectFromSteal: false,
              gain: 8,
            },
          })),
          // The chance barrel's activation, at the start of its `win` clip.
          {
            id: "grid_chance_collect",
            locator: { kind: "bundle_audio", bundle: AUDIO_BUNDLE, key: "audio/grid_chance_collect" },
            category: "sfx",
            defaultOpts: {
              priority: 5,
              stopOnSlam: false,
              protectFromSteal: false,
              gain:5
            },
          },
          {
            id: "grid_scatter_drop",
            locator: { kind: "bundle_audio", bundle: AUDIO_BUNDLE, key: "audio/grid_scatter_drop" },
            category: "sfx",
            defaultOpts: {
              priority: 5,
              stopOnSlam: false,
              protectFromSteal: false,
              gain:2
            },
          },
          {
            id: "grid_multiplier_drop",
            locator: { kind: "bundle_audio", bundle: AUDIO_BUNDLE, key: "audio/grid_multiplier_drop" },
            category: "sfx",
            defaultOpts: {
              priority: 5,
              stopOnSlam: false,
              protectFromSteal: false,
            },
          },
          {
            id: "grid_multiplier_collect",
            locator: { kind: "bundle_audio", bundle: AUDIO_BUNDLE, key: "audio/grid_multiplier_collect" },
            category: "sfx",
            defaultOpts: {
              priority: 5,
              stopOnSlam: false,
              protectFromSteal: false,
            },
          },
          {
            id: "grid_scatter_collect",
            locator: { kind: "bundle_audio", bundle: AUDIO_BUNDLE, key: "audio/grid_scatter_collect" },
            category: "sfx",
            defaultOpts: {
              priority: 5,
              stopOnSlam: false,
              protectFromSteal: false,
              gain:5
            },
          },
          // The feature-trigger panel's opening stinger (FEATURE_TRIGGER_TUNING.openSoundId).
          {
            id: "feature_trigger_panel_open",
            locator: { kind: "bundle_audio", bundle: AUDIO_BUNDLE, key: "audio/feature_trigger_panel_open" },
            category: "sfx",
            defaultOpts: {
              priority: 5,
              stopOnSlam: false,
              protectFromSteal: false,
              gain:5,
            },
          },
          // The feature-trigger panel's closing stinger (FEATURE_TRIGGER_TUNING.closeSoundId).
          {
            id: "feature_trigger_panel_close",
            locator: { kind: "bundle_audio", bundle: AUDIO_BUNDLE, key: "audio/feature_trigger_panel_close" },
            category: "sfx",
            defaultOpts: {
              priority: 5,
              stopOnSlam: false,
              protectFromSteal: false,
              gain:5,
            },
          },

          // ── slot-cannon feature music ──
          // Per-game feature music; another title's feature set supplies its own.
          {
            id: "music_feature_free_spins",
            locator: { kind: "bundle_audio", bundle: AUDIO_BUNDLE, key: "audio/music_feature_free_spins" },
            category: "music",
            defaultOpts: {
              gain: 1,
            },
          },
          {
            id: "music_feature_idle",
            locator: { kind: "bundle_audio", bundle: AUDIO_BUNDLE, key: "audio/music_feature_idle" },
            category: "music",
            defaultOpts: {
              gain: 0,
            },
          },
          {
            id: "music_feature_finished_intro",
            locator: { kind: "bundle_audio", bundle: AUDIO_BUNDLE, key: "audio/music_feature_finished_intro" },
            category: "music",
            defaultOpts: {
              gain: 1,
            },
          },
        ],
      },
    ],

    buses: {
      music: true,
      sfx: true,
      ui: true,
      voice: true,
      ambient: true,
    },

    // Per-bus linear volume. Tune to balance overall mix without re-mastering files.
    // For per-sound tuning, set `defaultOpts.gain` on the asset entry in the bank
    // (or pass a per-call `gain` via `playSoundId(id, { gain })`).
    busVolumes: {
      music: 0.8,
      sfx: 0.7,
      ui: 0.7,
      voice: 1.0,
      // Ambient mixes UNDER music (never replaces it) — kept low so the bed
      // sits beneath melodic content without competing. Tune per game.
      ambient: 0.8,
    },
  };
}